var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { isValid, parseISO } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { DAYS_IN_WEEK, ISO_FORMAT_PATTERN, ONE_DAY, SUNDAY_INDEX, } from './constants';
export var isFilledDate = function (value, options) {
    if (options === void 0) { options = {}; }
    var _a = options.pattern, pattern = _a === void 0 ? ISO_FORMAT_PATTERN : _a, _b = options.isPartial, isPartial = _b === void 0 ? false : _b;
    if (isRangeMode(value)) {
        return isPartial
            ? value.some(function (date) { return date && pattern.test(date); })
            : value.every(function (date) { return date && pattern.test(date); });
    }
    return pattern.test(value);
};
export var formatToISO = function (date, outputDateFormat) {
    var formatInTimeZone = function (date, fmt, tz) {
        return format(utcToZonedTime(date, tz), fmt, { timeZone: tz });
    };
    return formatInTimeZone(date, outputDateFormat, 'UTC');
};
export var isValidDateString = function (date) {
    var parsedDate = Date.parse(date);
    return isFilledDate(date) && !Number.isNaN(parsedDate);
};
export var getDateFromIsoString = function (date) {
    var isoDate = parseISO(date);
    return isValid(isoDate) ? isoDate : null;
};
export var isRangeMode = function (value) {
    return Array.isArray(value);
};
export var hasDateLimit = function (checkedDate, minDate, maxDate) {
    if (!checkedDate) {
        return false;
    }
    else if (minDate && !maxDate) {
        return checkedDate < minDate;
    }
    else if (maxDate && !minDate) {
        return checkedDate > maxDate;
    }
    else if (minDate && maxDate) {
        return checkedDate < minDate || checkedDate > maxDate;
    }
    return false;
};
export var isValidDateValue = function (value, minDate, maxDate) {
    if (isRangeMode(value)) {
        return (value.some(isValidDateString) &&
            value.some(function (isoDate) {
                return !hasDateLimit(getDateFromIsoString(isoDate), minDate, maxDate);
            }));
    }
    return (isValidDateString(value) &&
        !hasDateLimit(getDateFromIsoString(value), minDate, maxDate));
};
export var getViewDate = function (date, minDate, maxDate) {
    var viewDate = isRangeMode(date) ? date[0] : date;
    var currentDate = new Date();
    var viewDateFromIsoString = getDateFromIsoString(viewDate);
    var minDateFromIsoString = getDateFromIsoString(minDate);
    var maxDateFromIsoString = getDateFromIsoString(maxDate);
    if (viewDateFromIsoString) {
        return viewDateFromIsoString;
    }
    if (
    /**
     * Если текущая дата находится между мин и макс датами.
     */
    minDateFromIsoString &&
        minDateFromIsoString < currentDate &&
        maxDateFromIsoString &&
        maxDateFromIsoString > currentDate) {
        return currentDate;
    }
    else if (maxDateFromIsoString) {
        /**
         * Если текущая дата меньше макс даты
         */
        if (maxDateFromIsoString > currentDate) {
            return currentDate;
        }
        else {
            return maxDateFromIsoString;
        }
    }
    else if (minDateFromIsoString) {
        /**
         * Если текущая дата мин даты.
         */
        if (minDateFromIsoString < currentDate) {
            return currentDate;
        }
        else {
            return minDateFromIsoString;
        }
    }
    return currentDate;
};
export var isSameDates = function (firstDate, secondDate) {
    return firstDate.getFullYear() === secondDate.getFullYear() &&
        firstDate.getMonth() === secondDate.getMonth() &&
        firstDate.getDate() === secondDate.getDate();
};
export var isSameMonths = function (firstDate, secondDate) {
    return firstDate.getFullYear() === secondDate.getFullYear() &&
        firstDate.getMonth() === secondDate.getMonth();
};
var isSunday = function (dayIndex) { return dayIndex === SUNDAY_INDEX; };
var getFirstDayOfWeekInMonth = function (year, month) {
    return new Date(year, month, 1).getDay();
};
var countDaysInMonth = function (year, month) {
    return new Date(year, month + 1, 0).getDate();
};
var countWeeksInMonth = function (firstMonthDay, totalDaysInMonth) {
    var correctionIndex = isSunday(firstMonthDay) ? 6 : -1;
    return Math.ceil((firstMonthDay + totalDaysInMonth + correctionIndex) / DAYS_IN_WEEK);
};
var getDaysListOfMonth = function (totalDaysInMonth) {
    return Array.from({ length: totalDaysInMonth }, function (_, i) { return i + 1; });
};
export var getWeeksOfMonth = function (year, month) {
    var weeksOfMonth = [];
    var firstDayOfWeekInMonth = getFirstDayOfWeekInMonth(year, month);
    var totalDaysInMonth = countDaysInMonth(year, month);
    var totalWeeksInMonth = countWeeksInMonth(firstDayOfWeekInMonth, totalDaysInMonth);
    /* Создаем массив всех дней месяца - [1, 2, 3, 4, 5, 6, 7, 8, 9, ...] */
    var daysListOfMonth = getDaysListOfMonth(totalDaysInMonth);
    for (var weekNumber = 1; weekNumber <= totalWeeksInMonth; weekNumber++) {
        /* Инициализируем массив текущей недели заполненный null'ами - [null x 7] */
        var currentWeek = new Array(DAYS_IN_WEEK).fill(null);
        var isFirstWeek = weekNumber === 1;
        /* Обрабатываем первую неделю месяца отдельно,
         * так как количество недель в месяце не кратко общему количеству дней
         */
        if (isFirstWeek) {
            /* Находим количество пустых (отсутствующих) дней первой недели месяца отображаемых в календаре */
            var totalEmptyDaysInFirstWeek = isSunday(firstDayOfWeekInMonth)
                ? DAYS_IN_WEEK - ONE_DAY
                : firstDayOfWeekInMonth - ONE_DAY;
            var totalDaysInCurrentWeek = isSunday(firstDayOfWeekInMonth)
                ? ONE_DAY
                : DAYS_IN_WEEK - totalEmptyDaysInFirstWeek;
            var daysListOfCurrentWeek = daysListOfMonth.splice(0, totalDaysInCurrentWeek);
            /* Заполняем массив текущей (первой) недели,
             * учитывая возможные пустые дни с начала месяца - [null, null, 1, 2, 3, 4, 5]
             */
            currentWeek.splice.apply(currentWeek, __spreadArray([totalEmptyDaysInFirstWeek,
                daysListOfCurrentWeek.length], daysListOfCurrentWeek, false));
        }
        else {
            var daysListOfCurrentWeek = daysListOfMonth.splice(0, DAYS_IN_WEEK);
            /* Для остальных недель учет оставшихся пустых дней последней недели
             * будет происходить автоматически - [27, 28, 29, 30, 31, null, null]
             */
            currentWeek.splice.apply(currentWeek, __spreadArray([0,
                daysListOfCurrentWeek.length], daysListOfCurrentWeek, false));
        }
        weeksOfMonth.push(currentWeek);
    }
    return weeksOfMonth;
};
export var getWeeksOfMonths = function (year) {
    var monthsNumbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    return [year - 1, year, year + 1].reduce(function (monthsWeeks, currentYear) {
        var newMonthWeeks = monthsNumbers.map(function (monthNumber) { return ({
            year: currentYear,
            month: monthNumber,
            monthWeeks: getWeeksOfMonth(currentYear, monthNumber),
        }); });
        return __spreadArray(__spreadArray([], monthsWeeks, true), newMonthWeeks, true);
    }, []);
};
